<script setup lang="ts">
import { copyToClipboard } from '@/shared/utils/copyToClipboard';
import FavoriteButton from '@/features/FavoriteButton/index';
import ContextMenu from '@/features/ContextMenu/index';
import LawyerCardContextContent from '@/widgets/LawyerCard/ui/LawyerCardContextContent.vue';
import { useModalStore } from '@/shared/ui/AppModal/store/modal';
import type { ILawyerCardActionsProps } from '@/widgets/LawyerCard/model/types/lawyerCard';
import type { ILawyerCardInfo } from '@/entities/lawyer/model/types/lawyer';
import { useErrorHandler } from '@/shared/lib/errors';

withDefaults(defineProps<ILawyerCardActionsProps>(), {
  isLawyerPage: false,
});

const { $favoriteService } = useNuxtApp();
const modalStore = useModalStore();
const { handleError } = useErrorHandler();
const lawyerInfo = inject<ILawyerCardInfo>('lawyerInfo');

const isFavorite: Ref<boolean> = ref(Boolean(lawyerInfo?.isFavorite));

const toggleFavorite = async (status: boolean): Promise<void> => {
  const lawyerId = lawyerInfo?.id;
  if (!lawyerId) return;

  isFavorite.value = status;
  try {
    if (status) {
      await $favoriteService.addToFavorite(lawyerId);
    } else {
      await $favoriteService.deleteFromFavorite(lawyerId);
    }
  } catch (e) {
    isFavorite.value = !status;
    handleError(e);
  }
};

const copyUrl = async (): Promise<void> => {
  await copyToClipboard(window.location.href);
};

const showFeedback = (): void => {
  modalStore.setModalState('complaint', true, { lawyerInfo });
};

watch(
  () => lawyerInfo?.isFavorite,
  (value) => {
    isFavorite.value = Boolean(value);
  },
);
</script>

<template>
  <div class="lawyer-card-actions">
    <div class="lawyer-card-actions__favorite">
      <FavoriteButton :model-value="isFavorite" @update:model-value="toggleFavorite" />
    </div>
    <div v-if="isLawyerPage" class="lawyer-card-actions__context">
      <ContextMenu>
        <LawyerCardContextContent is-lawyer-card @show="showFeedback" @copy="copyUrl" />
      </ContextMenu>
    </div>
  </div>
</template>

<style lang="less">
.lawyer-card-actions {
  display: flex;
  align-items: center;
  position: absolute;
  right: 0;
  top: 0;

  &__context {
    margin-right: -@size-x2;
    margin-left: @main-size;
  }
}
</style>
